// File: client/src/variants/uno/useUnoRuntime.js
//
// UNO X CHESS as a platform plugin runtime (a React hook). Owns all UNO state
// (deck / discard / hands / effects / turn / skips / control) and returns a
// VariantRuntime the Variant Host wires into GameScreen. The host handles the
// generic game loop and persistence; this hook just exposes:
//   moveMiddleware, renderOverlay(slot), getPersistencePayload(), plus card play.
//
// This is a faithful port of the former components/VariantController.js logic; the
// only structural change is that persistence is now an OPAQUE payload the host
// stores (getPersistencePayload) rather than UNO calling localStorage directly.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import CardHand from '../../components/CardHand';
import ActiveEffectsDisplay from '../../components/ActiveEffectsDisplay';
import { registerAllEffects } from '../../variant/cardEffects';
import { generateDeck, drawCard } from '../../variant/deck';
import { createTurnState, applyCardOnPlay, getEffect } from '../../variant/effectEngine';
import { INITIAL_GAME_STATE } from '../../utils/chessLogic';

export function useUnoRuntime({ config, resumePayload, chess }) {
  const [deck, setDeck] = useState([]);
  const [discard, setDiscard] = useState([]);
  const [whiteHand, setWhiteHand] = useState([]);
  const [blackHand, setBlackHand] = useState([]);
  const [activeEffects, setActiveEffects] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState('white');
  const [turnState, setTurnState] = useState(null);
  const [skips, setSkips] = useState({ white: 0, black: 0 });

  const [humanColor, setHumanColor] = useState('white');
  const [aiColor, setAiColor] = useState('black');
  const [boardFlipped, setBoardFlipped] = useState(false);
  const [pendingImmediateMoves, setPendingImmediateMoves] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  // Refs to avoid stale closures inside GameScreen's async callbacks.
  const deckRef = useRef([]);
  const whiteHandRef = useRef([]);
  const blackHandRef = useRef([]);
  const currentPlayerRef = useRef('white');
  const turnStateRef = useRef(null);
  const skipsRef = useRef({ white: 0, black: 0 });
  const pendingSwapControlRef = useRef(false);
  const catalogRef = useRef([]);
  const lastGameStateRef = useRef(INITIAL_GAME_STATE);

  useEffect(() => { deckRef.current = deck; }, [deck]);
  useEffect(() => { whiteHandRef.current = whiteHand; }, [whiteHand]);
  useEffect(() => { blackHandRef.current = blackHand; }, [blackHand]);
  useEffect(() => { currentPlayerRef.current = currentPlayer; }, [currentPlayer]);
  useEffect(() => { turnStateRef.current = turnState; }, [turnState]);
  useEffect(() => { skipsRef.current = skips; }, [skips]);

  // Keep lastGameStateRef fresh from the host's chess ref (read on demand too).
  useEffect(() => {
    if (chess && chess.current && chess.current.gameState) {
      lastGameStateRef.current = chess.current.gameState;
    }
  });

  // ---- initialization: resume from payload, else fetch catalog + deal ----
  useEffect(() => {
    registerAllEffects();
    let cancelled = false;

    const setup = async () => {
      // Resume path: the host passes our previously persisted payload.
      if (resumePayload && resumePayload.deck) {
        setDeck(resumePayload.deck);
        setDiscard(resumePayload.discard || []);
        setWhiteHand(resumePayload.whiteHand || []);
        setBlackHand(resumePayload.blackHand || []);
        setCurrentPlayer(resumePayload.currentPlayer || 'white');
        setTurnState(resumePayload.turnState || createTurnState(resumePayload.currentPlayer || 'white'));
        setSkips(resumePayload.skips || { white: 0, black: 0 });
        setHumanColor(resumePayload.humanColor || 'white');
        setAiColor(resumePayload.aiColor || 'black');
        setBoardFlipped(!!resumePayload.boardFlipped);
        catalogRef.current = resumePayload.catalog || [];
        setStatus('ready');
        return;
      }

      let catalog = [];
      try {
        const res = await fetch('/api/games/cards');
        if (!res.ok) throw new Error(`catalog fetch failed: ${res.status}`);
        catalog = await res.json();
      } catch (err) {
        if (!cancelled) setStatus('error');
        console.error('UNO X CHESS: failed to load card catalog:', err.message);
        return;
      }
      if (cancelled) return;
      if (!Array.isArray(catalog) || catalog.length === 0) { setStatus('error'); return; }

      const seed = Math.floor(Math.random() * 1000000);
      let currentDeck = generateDeck(seed, catalog);
      let wHand = [];
      let bHand = [];
      for (let i = 0; i < 3; i++) {
        const wDraw = drawCard(currentDeck, wHand, 5);
        currentDeck = wDraw.deck; wHand = wDraw.hand;
        const bDraw = drawCard(currentDeck, bHand, 5);
        currentDeck = bDraw.deck; bHand = bDraw.hand;
      }
      catalogRef.current = catalog;
      setDeck(currentDeck);
      setWhiteHand(wHand);
      setBlackHand(bHand);
      setCurrentPlayer('white');
      setTurnState(createTurnState('white'));
      setStatus('ready');
    };

    setup();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // ---- card play ----
  const handlePlayCard = useCallback((card) => {
    const ts = turnStateRef.current;
    if (!ts || ts.cardPlayed) return;

    const player = currentPlayerRef.current;
    const opponentColor = player === 'white' ? 'black' : 'white';
    const ctx = { gameState: lastGameStateRef.current, playerColor: player, opponentColor };
    const { directive, turnState: nextTurnState } = applyCardOnPlay(card, ctx, ts);

    if (player === 'white') {
      setWhiteHand(prev => prev.filter(c => c.instance_id !== card.instance_id));
    } else {
      setBlackHand(prev => prev.filter(c => c.instance_id !== card.instance_id));
    }
    setDiscard(prev => [...prev, card]);

    if (directive.swapControl) {
      pendingSwapControlRef.current = true;
      setBoardFlipped(prev => !prev);
    }
    if (directive.tradeHands) {
      const cw = whiteHandRef.current;
      const cb = blackHandRef.current;
      setWhiteHand(cb);
      setBlackHand(cw);
    }
    if (directive.skipOpponentTurns > 0) {
      setSkips(prev => ({ ...prev, [opponentColor]: prev[opponentColor] + directive.skipOpponentTurns }));
    }
    if (directive.immediateMoves && directive.immediateMoves.length > 0) {
      setPendingImmediateMoves(directive.immediateMoves);
    }
    setTurnState(nextTurnState);
    setActiveEffects(prev => [...prev, { name: card.name, kind: card.effect_json.kind }]);
  }, []);

  // ---- move middleware ----
  const moveMiddleware = {
    beforeMove(gameState, from) {
      const ts = turnStateRef.current;
      if (!ts) return { allow: true };
      const bonusEffect = getEffect('bonus_moves');
      const veto = bonusEffect.beforeMove({
        turnState: ts,
        from: [from.row, from.col],
        lastMovedPieceKey: ts.lastMovedPieceKey,
      });
      if (veto && !veto.allow) return veto;
      return { allow: true };
    },

    afterMove(newState, from, to) {
      lastGameStateRef.current = newState;
      const ts = { ...turnStateRef.current };
      ts.movesMade += 1;
      ts.lastMovedPieceKey = `${to.row},${to.col}`;

      const activeColor = currentPlayerRef.current;
      const opponentColor = activeColor === 'white' ? 'black' : 'white';

      if (ts.movesMade >= ts.movesAllowed) {
        let nextDeck = deckRef.current;
        let nextWhiteHand = whiteHandRef.current;
        let nextBlackHand = blackHandRef.current;

        if (activeColor === 'white') {
          const draw = drawCard(nextDeck, nextWhiteHand, 5);
          nextDeck = draw.deck; nextWhiteHand = draw.hand;
        } else {
          const draw = drawCard(nextDeck, nextBlackHand, 5);
          nextDeck = draw.deck; nextBlackHand = draw.hand;
        }
        setActiveEffects([]);

        const currentSkips = { ...skipsRef.current };
        if (currentSkips[opponentColor] > 0) {
          currentSkips[opponentColor] -= 1;
          if (activeColor === 'white') {
            const draw = drawCard(nextDeck, nextWhiteHand, 5);
            nextDeck = draw.deck; nextWhiteHand = draw.hand;
          } else {
            const draw = drawCard(nextDeck, nextBlackHand, 5);
            nextDeck = draw.deck; nextBlackHand = draw.hand;
          }
          setDeck(nextDeck);
          setWhiteHand(nextWhiteHand);
          setBlackHand(nextBlackHand);
          setSkips(currentSkips);
          setTurnState(createTurnState(activeColor));
          return { turnEnded: false };
        }

        const newTS = createTurnState(opponentColor);
        setDeck(nextDeck);
        setWhiteHand(nextWhiteHand);
        setBlackHand(nextBlackHand);
        setTurnState(newTS);
        setCurrentPlayer(opponentColor);

        if (pendingSwapControlRef.current) {
          pendingSwapControlRef.current = false;
          setHumanColor(prev => (prev === 'white' ? 'black' : 'white'));
          setAiColor(prev => (prev === 'white' ? 'black' : 'white'));
        }
        return { turnEnded: true };
      }

      setTurnState(ts);
      return { turnEnded: false };
    },

    transformLegalMoves(gameState, fromSquare, engineMoves) {
      const ts = turnStateRef.current;
      if (!ts || !ts.rules) return engineMoves;
      let moves = [...engineMoves];
      const rules = ts.rules;

      if (rules.movementOverride === 'knight') {
        const eff = getEffect('movement_override');
        if (typeof eff.transformLegalMoves === 'function') {
          moves = eff.transformLegalMoves({ gameState, from: fromSquare, engineMoves: moves, rules });
        }
      }
      if (rules.movementLimit) {
        const eff = getEffect('movement_limit');
        if (typeof eff.transformLegalMoves === 'function') {
          moves = eff.transformLegalMoves({ gameState, from: fromSquare, engineMoves: moves, rules });
        }
      }
      return moves;
    },
  };

  // ---- persistence payload (opaque to the host) ----
  const getPersistencePayload = useCallback(() => {
    if (status !== 'ready') return null;
    return {
      deck, discard, whiteHand, blackHand, currentPlayer, turnState, skips,
      humanColor, aiColor, boardFlipped, catalog: catalogRef.current,
    };
  }, [status, deck, discard, whiteHand, blackHand, currentPlayer, turnState,
      skips, humanColor, aiColor, boardFlipped]);

  // ---- overlay rendering ----
  const isVsAI = config.opponent === 'ai';
  const showHandColor = isVsAI ? humanColor : currentPlayer;
  const isHandInteractive = isVsAI ? currentPlayer === humanColor : true;
  const currentHand = showHandColor === 'white' ? whiteHand : blackHand;

  const renderOverlay = (slot) => {
    if (status !== 'ready') {
      // Minimal inline status while the deck loads / on error.
      if (slot === 'top') {
        return status === 'error'
          ? <p className="text-red-300 text-sm">Card deck unavailable — playing without cards.</p>
          : <p className="text-slate-300 text-sm">🃏 Shuffling deck…</p>;
      }
      return null;
    }
    if (slot === 'top') {
      return <ActiveEffectsDisplay activeEffects={activeEffects} turnState={turnState} />;
    }
    if (slot === 'bottom') {
      return (
        <CardHand
          hand={currentHand}
          activeEffects={activeEffects}
          turnState={turnState}
          isActiveTurn={isHandInteractive}
          onPlayCard={handlePlayCard}
          playerColor={showHandColor}
        />
      );
    }
    return null;
  };

  // Expose flipped/aiColor + pendingMoves via the runtime so the host can pass them.
  return {
    moveMiddleware,
    renderOverlay,
    getPersistencePayload,
    // UNO-specific board props the host forwards to GameScreen:
    boardFlipped,
    aiColor,
    pendingMoves: pendingImmediateMoves,
    onMovesApplied: () => setPendingImmediateMoves(null),
  };
}
